import { useState, type FormEvent } from 'react';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth, type SecondFactorChallenge, type SecondFactorKind } from '@/auth';
import { messages } from '@/i18n';
import { ErrorAlert } from './mfaShared';
import { INPUT_CLASS, secondFactorErrorMessage } from './mfaUtils';
import { SecondFactorChallengeForm } from './SecondFactorChallengeForm';

/**
 * Asked for when the provider refuses a change with
 * RecentSignInRequiredError: the password again, then the second factor if one
 * is enrolled. `onDone` resumes whatever was refused.
 */
export function ReauthenticateForm({
  onDone,
  onCancel,
  preferKind,
}: {
  onDone: () => void;
  onCancel: () => void;
  preferKind?: SecondFactorKind | undefined;
}) {
  const t = messages.mfa;
  const { reauthenticate } = useAuth();
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<SecondFactorChallenge | null>(null);

  if (challenge) {
    return (
      <SecondFactorChallengeForm
        challenge={challenge}
        onSuccess={onDone}
        onCancel={onCancel}
        preferKind={preferKind}
      />
    );
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);
    reauthenticate(password)
      .then((result) => {
        if (result.status === 'second-factor-required') setChallenge(result.challenge);
        else onDone();
      })
      .catch((err: unknown) => setError(secondFactorErrorMessage(err)))
      .finally(() => setPending(false));
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <div>
        <h2 className="text-base font-semibold">{t.reauthTitle}</h2>
        <p className="text-muted-foreground mt-1 text-sm">{t.reauthBody}</p>
      </div>
      <ErrorAlert message={error} />
      <label className="flex flex-col gap-1 text-sm">
        {t.reauthPasswordLabel}
        <input
          type="password"
          required
          autoComplete="current-password"
          className={INPUT_CLASS}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={pending}
        />
      </label>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button type="submit" className="flex-1" disabled={pending || !password}>
          <KeyRound aria-hidden />
          {t.reauthSubmit}
        </Button>
      </div>
    </form>
  );
}
